import { useState } from "react"
import { useNavigate } from "react-router-dom"
import AddIcon from "@mui/icons-material/Add"
import BorderColorIcon from "@mui/icons-material/BorderColor"
import SearchIcon from "@mui/icons-material/Search"
import DeleteIcon from "@mui/icons-material/Delete"
import DeleteBranch from "../../../components/Staff/Modals/DeleteBranch"
import UpdateLeadModel from "../../../components/Staff/Modals/UpdateLeadModel"
import { IoMdArrowDropright } from "react-icons/io"
import Cookies from "js-cookie"
import { useGlobalContext } from "../../../Context/GlobalContext"
import { saveAs } from "file-saver"
import jsPDF from "jspdf"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"

const TableRowSkeleton = () => (
  <tr className="border-gray-300 border-b">
    {[...Array(17)].map((_, index) => (
      <td key={index} className="p-2 border-[#dbdbdb] border-r text-center">
        <Skeleton width={index === 0 ? 30 : 80} />
      </td>
    ))}
  </tr>
)

const TableHeaderSkeleton = () => (
  <tr>
    {[...Array(17)].map((_, index) => (
      <th key={index} className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">
        <Skeleton width={index === 0 ? 30 : 80} />
      </th>
    ))}
  </tr>
)

const PaginationSkeleton = () => (
  <div className="flex gap-2 pt-0 pr-4 pb-0 pl-4 border rounded-md">
    <div className="px-2 pt-2 pb-[8px]">
      <Skeleton width={60} />
    </div>
    <div className="px-3 pt-2 pb-[8px]">
      <Skeleton width={20} />
    </div>
    <div className="px-2 pt-2 pb-[8px]">
      <Skeleton width={40} />
    </div>
  </div>
)

const LeadDetail = () => {
  const navigate = useNavigate()
  const {
    baseUrl,
    openToast,
    leads,
    setleads,
    currentleadPage,
    setCurrentleadPage,
    totalleadPage,
    setTotalleadPage,
    totalleadRecords,
    setTotalleadRecords,
    pagelead,
    setleadPage,
    fetchLead,
  } = useGlobalContext()

  const [id, setId] = useState(null)
  const [open, setOpen] = useState(false)
  const onOpenModal = () => setOpen(true)
  const onCloseModal = () => setOpen(false)
  const [exportFormat, setExportFormat] = useState("CSV")
  const [isDownloading, setIsDownloading] = useState(false)
  const [rowsToShow, setRowsToShow] = useState(25)
  const [searchLeadName, setSearchLeadName] = useState("")
  const [isSearching, setIsSearching] = useState(false)

  const handleSearch = async (e) => {
    const value = e.target.value
    setSearchLeadName(value)

    if (value.trim() === "") {
      fetchLead()
      return
    }

    setIsSearching(true)
    try {
      const response = await fetch(`${baseUrl}lead/search?name=${value}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("flowChangerAuthToken")}`,
        },
      })
      const data = await response.json()
      if (response.ok) {
        setleads(data || [])
      } else {
        openToast(data.message, "error")
      }
    } catch (error) {
      console.error("Error searching roles:", error)
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectChange = (event) => {
    setRowsToShow(Number(event.target.value))
  }

  const fetchData = async () => {
    const result = await fetch(`${baseUrl}/lead?limit=${rowsToShow}`, {
      headers: {
        Authorization: `Bearer ${Cookies.get("flowChangerAuthToken")}`,
      },
    })
    const data = await result.json()
    return data.leads
  }

  const handleExport = () => {
    if (exportFormat === "CSV") exportCSV()
    else if (exportFormat === "PDF") exportPDF()
    else if (exportFormat === "Print") printContent()
    fetchData()
  }

  const exportCSV = async () => {
    setIsDownloading(true)
    try {
      const headers = [
        "Name",
        "Position",
        "Company",
        "City",
        "Email",
        "State",
        "Website",
        "Country",
        "Phone",
        "Zip Code",
        "Lead Value",
        "Default Language",
        "Tags",
        "Address",
        "Description",
        "Date Created",
      ]

      const d = await fetchData()
      console.log("Leads", d)

      const csvData = [
        headers.join(","),
        ...d.map((lead) => {
          const currency = Array.isArray(lead?.currencySymbol) ? lead.currencySymbol.join(",") : "N/A"
          const default_language = Array.isArray(lead?.defaultLanguage) ? lead.defaultLanguage.join(",") : "N/A"
          const leadValue = Array.isArray(lead?.leadValue) ? lead.leadValue.join(",") : "N/A"
          const tags = Array.isArray(lead?.tags) ? lead.tags.join(",") : "N/A"

          return [
            `"${lead?.name ?? "N/A"}"`,
            `"${lead?.position ?? "N/A"}"`,
            `"${lead?.company ?? "N/A"}"`,
            `"${lead?.city ?? "N/A"}"`,
            `"${lead?.email ?? "N/A"}"`,
            `"${lead?.state ?? "N/A"}"`,
            `"${lead?.website ?? "N/A"}"`,
            `"${lead?.country ?? "N/A"}"`,
            `"${lead?.phone ?? "N/A"}"`,
            `"${lead?.zipCode ?? "N/A"}"`,
            `"${leadValue}"`,
            `"${default_language}"`,
            `"${tags}"`,
            `"${lead?.address ?? "N/A"}"`,
            `"${lead?.description ?? "N/A"}"`,
            `"${new Date(lead?.created_at).toDateString() ?? "N/A"}"`,
          ].join(",")
        }),
      ].join("\n")

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
      saveAs(blob, "leads.csv")
    } catch (error) {
      console.log(error)
      openToast("An unexpected error occurred. Please try again.", "error")
    } finally {
      setIsDownloading(false)
    }
  }

  const exportPDF = async () => {
    setIsDownloading(true)
    try {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      })

      // Define table headers with more concise names
      const headers = [
        [
          "Name",
          "Position",
          "Company",
          "City",
          "Email",
          "State",
          "Website",
          "Country",
          "Phone",
          "Zip",
          "Value",
          "Language",
          "Tags",
          "Address",
          "Created",
        ],
      ]

      // Fetch data
      const d = await fetchData()

      // Map data into rows with truncated content
      const rows = d.map((lead) => {
        // Helper function to truncate text
        const truncate = (str, length) => {
          if (!str) return "N/A"
          return str.length > length ? str.substring(0, length) + "..." : str
        }

        const tags = lead?.tags?.join(", ") ?? "N/A"
        const leadValue = lead?.leadValue ?? "N/A"
        const default_language = lead?.defaultLanguage?.join(", ") ?? "N/A"

        return [
          truncate(lead?.name ?? "N/A", 20),
          truncate(lead?.position ?? "N/A", 20),
          truncate(lead?.company ?? "N/A", 20),
          truncate(lead?.city ?? "N/A", 15),
          truncate(lead?.email ?? "N/A", 25),
          truncate(lead?.state ?? "N/A", 15),
          truncate(lead?.website ?? "N/A", 25),
          truncate(lead?.country ?? "N/A", 15),
          truncate(lead?.phone ?? "N/A", 15),
          truncate(lead?.zipCode ?? "N/A", 10),
          truncate(leadValue, 10),
          truncate(default_language, 15),
          truncate(tags, 20),
          truncate(lead?.address ?? "N/A", 25),
          new Date(lead?.created_at).toLocaleDateString() ?? "N/A",
        ]
      })

      // Add title
      doc.setFontSize(16)
      doc.setTextColor(44, 62, 80)
      doc.text("Leads Data", 14, 15)

      // Add the table with optimized configuration
      doc.autoTable({
        head: headers,
        body: rows,
        startY: 25,
        styles: {
          fontSize: 8,
          cellPadding: 2,
          lineColor: [189, 195, 199],
          lineWidth: 0.1,
          minCellHeight: 8,
          valign: "middle",
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [22, 160, 133],
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: "bold",
          halign: "center",
          cellPadding: 3,
        },
        columnStyles: {
          0: { cellWidth: 20 }, // Name
          1: { cellWidth: 20 }, // Position
          2: { cellWidth: 20 }, // Company
          3: { cellWidth: 15 }, // City
          4: { cellWidth: 25 }, // Email
          5: { cellWidth: 15 }, // State
          6: { cellWidth: 25 }, // Website
          7: { cellWidth: 15 }, // Country
          8: { cellWidth: 15 }, // Phone
          9: { cellWidth: 12 }, // Zip
          10: { cellWidth: 12 }, // Value
          11: { cellWidth: 15 }, // Language
          12: { cellWidth: 20 }, // Tags
          13: { cellWidth: 25 }, // Address
          14: { cellWidth: 15 }, // Created
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        margin: { top: 25, right: 10, bottom: 15, left: 10 },
        didDrawPage: (data) => {
          // Add page number
          doc.setFontSize(8)
          doc.setTextColor(128, 128, 128)
          doc.text(`Page ${data.pageNumber}`, data.settings.margin.left, doc.internal.pageSize.height - 10)
        },
      })

      // Save the PDF
      doc.save("LeadsData.pdf")
    } catch (error) {
      openToast("An unexpected error occurred. Please try again.", "error")
    } finally {
      setIsDownloading(false)
    }
  }

  const printContent = async () => {
    setIsDownloading(true)

    // Open a blank window immediately
    const newWindow = window.open("", "_blank")

    if (!newWindow) {
      openToast("Unable to open a new window. Please allow pop-ups in your browser.", "error")
      setIsDownloading(false)
      return
    }

    try {
      const d = await fetchData()

      const printContent = `
        <html>
          <head>
            <style>
              table {
                width: 100%;
                border-collapse: collapse;
                margin: 20px 0;
              }
              table, th, td {
                border: 1px solid #ccc;
              }
              th, td {
                padding: 10px;
                text-align: left;
              }
              th {
                background-color: #f4f4f4;
              }
              h1 {
                text-align: center;
              }
            </style>
          </head>
          <body>
            <h1>Leads Details</h1>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Position</th>
                  <th>Company</th>
                  <th>City</th>
                  <th>Email</th>
                  <th>State</th>
                  <th>Website</th>
                  <th>Country</th>
                  <th>Phone</th>
                  <th>Zip Code</th>
                  <th>Lead Value</th>
                  <th>Default Language</th>
                  <th>Company</th>
                  <th>Tags</th>
                  <th>Address</th>
                  <th>Description</th>
                  <th>Created At</th>
                </tr>
              </thead>
              <tbody>
                ${d
                  .map((lead) => {
                    return `
                  <tr>
                    <td>${lead?.name || "N/A"}</td>
                    <td>${lead?.position || "N/A"}</td>
                    <td>${lead?.company || "N/A"}</td>
                    <td>${lead?.city || "N/A"}</td>
                    <td>${lead?.email || "N/A"}</td>
                    <td>${lead?.state || "N/A"}</td>
                    <td>${lead?.website || "N/A"}</td>
                    <td>${lead?.country || "N/A"}</td>
                    <td>${lead?.phone || "N/A"}</td>
                    <td>${lead?.zipCode || "N/A"}</td>
                    <td>${lead?.leadValue || "N/A"}</td>
                    <td>${lead?.defaultLanguage || "N/A"}</td>
                    <td>${lead?.company || "N/A"}</td>
                    <td>${lead?.tags || "N/A"}</td>
                    <td>${lead?.address || "N/A"}</td>
                    <td>${lead?.description || "N/A"}</td>
                    <td>${new Date(lead.created_at).toDateString() || "N/A"}</td>
                  </tr>`
                  })
                  .join("")}
              </tbody>
            </table>
          </body>
        </html>
      `

      // Write content to the new window
      newWindow.document.open()
      newWindow.document.write(printContent)
      newWindow.document.close()

      // Ensure the window is focused and print
      newWindow.focus()
      newWindow.print()
      newWindow.close()
    } catch (error) {
      openToast("An unexpected error occurred. Please try again.", "error")
    } finally {
      setIsDownloading(false)
    }
  }

  const handlePrevious = () => {
    if (currentleadPage <= 1) return
    const prevPage = currentleadPage - 1
    setCurrentleadPage(prevPage)
    fetchLead(prevPage)
  }

  const handleNext = () => {
    if (currentleadPage >= totalleadPage) return
    const nextPage = currentleadPage + 1
    setCurrentleadPage(nextPage)
    fetchLead(nextPage)
  }
  const [isOpen, setIsOpen] = useState(true)
  const [selectedItem, setSelectedItem] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const toggleAccordion = () => {
    setIsOpen(!isOpen)
  }

  async function deleteLead(id) {
    try {
      const response = await fetch(baseUrl + `lead/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Cookies.get("flowChangerAuthToken")}`,
        },
      })

      if (response.ok) {
        const result = await response.json()
        openToast("Delete Lead successfully:", result)
        fetchLead(pagelead) //Call fetchLead with current page
        setId(null)
      } else {
        openToast("Something went wrong", "error")
      }
    } catch (error) {
      console.log("error", error)
    }
  }

  return (
    <>
      <DeleteBranch id={id} setId={setId} deleteBranch={deleteLead} />
      <UpdateLeadModel selectedItem={selectedItem} open={open} callback={fetchLead} onCloseModal={onCloseModal} />
      <div className="bg-white px-4 py-6 w-full">
        <button
          onClick={() => {
            navigate("/add-lead")
          }}
          className="allcrm-btn"
        >
          <AddIcon className="addicon-all" /> New Lead
        </button>
        {/* <Link to="/add-lead" className="allcrm-btn">
        </Link> */}

        <div className="table-section bg-white shadow-cs mt-5 p-[20px] rounded-lg">
          <div className="flex sm:flex-row flex-col justify-between items-center gap-2 mb-3">
            <div className="flex sm:flex-row flex-col flex-wrap items-center gap-2 w-full">
              <select
                onChange={handleSelectChange}
                className="shadow-sm p-[7px] border border-[#e5e7eb] rounded-md focus:outline-none w-full sm:w-[60px] text-[14px]"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="120">120</option>
              </select>
              <select
                onChange={(e) => setExportFormat(e.target.value)}
                className="shadow-sm p-[7px] border border-[#e5e7eb] rounded-md focus:outline-none w-full sm:w-[67px] text-[14px]"
              >
                <option value="CSV">CSV</option>
                <option value="PDF">PDF</option>
                <option value="Print">Print</option>
              </select>

              <button
                onClick={handleExport}
                className="bg-[#27004a] p-[7px] rounded-md w-full sm:w-[123px] text-[14px] text-white cursor-pointer"
              >
                {isDownloading ? "Exporting..." : "Export File"}
              </button>
            </div>
            <div className="relative flex flex-wrap justify-end items-center gap-2 w-full">
              <input
                type="text"
                placeholder="Search"
                value={searchLeadName}
                onChange={handleSearch}
                className="p-[10px] pr-7 border border-1 rounded-3xl focus:outline-none w-[100%] md:w-[200px] h-[38px] text-[#000] text-[15px] ]"
              />
              <SearchIcon className="right-[10px] absolute search-icon font-thin text-[#000] text-[#dddddd; ]" />
            </div>
          </div>
          <div className="bg-white shadow-cs border border-[#dcdbdb] rounded-lg w-full overflow-x-auto sid">
            <table className="border border-[#dcdbdb] rounded-lg w-full overflow-hidden border-collapse table-auto">
              {/* Header with Toggle */}
              <thead className="shadow-md border border-gray-300 cursor-pointer" onClick={toggleAccordion}>
                {isLoading ? (
                  <TableHeaderSkeleton />
                ) : (
                  <tr>
                    <th className="flex justify-center items-center p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">
                      <IoMdArrowDropright
                        className={`text-[20px] transition-transform duration-200 ${
                          isOpen ? "rotate-90 text-[black]" : "rotate-0"
                        }`}
                      />
                    </th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Name</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Position</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Company</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">City</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap"> Email</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">State</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Website</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Country</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Phone</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Zip Code</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Lead Value</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">
                      Default Language
                    </th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Company</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Tags</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Address</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Description</th>
                    <th className="p-3 border-r font-medium text-[13px] text-center whitespace-nowrap">Action</th>
                  </tr>
                )}
              </thead>

              <tbody className="transition-all duration-300">
                {isLoading ? (
                  [...Array(10)].map((_, index) => <TableRowSkeleton key={index} />)
                ) : !isSearching && leads?.length > 0 ? (
                  leads.map((item, index) => {
                    const rowNumber = (currentleadPage - 1) * 10 + (index + 1)
                    return (
                      <tr key={index} className="border-gray-300 border-b">
                        <td className="p-2 border-[#dbdbdb] border-r text-center">{rowNumber}</td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.name}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.position}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.company}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.city}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.email}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.state}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.website ? (
                            <span title={item.website}>
                              {item.website.length > 20 ? item.website.substring(0, 20) + "..." : item.website}
                            </span>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.country}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.phone}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.zipCode}
                        </td>

                        <td className="p-2 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.leadValue?.map((leadValue, index) => (
                            <span
                              className="mr-[4px] p-[4px] border border-[#dbdbdb] rounded-md font-medium text-[#27004a] cursor-pointer"
                              key={index}
                            >
                              {leadValue}
                            </span>
                          ))}
                        </td>

                        <td className="flex justify-center items-center gap-[4px] p-2 border-[rgb(219,219,219)] border-r text-xs text-center whitespace-nowrap">
                          {item?.defaultLanguage?.map((defaultLanguage, index) => (
                            <span
                              className="p-[4px] border border-[#dbdbdb] rounded-md font-medium text-[#27004a] cursor-pointer"
                              key={index}
                            >
                              {defaultLanguage}
                            </span>
                          ))}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.company}
                        </td>
                        <td className="p-2 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap detail-lead">
                          {item?.tags?.map((tag, index) => (
                            <span
                              className="p-[4px] border border-[#dbdbdb] rounded-md font-medium text-[#27004a] cursor-pointer"
                              key={index}
                            >
                              {tag}
                            </span>
                          ))}
                        </td>

                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.address}
                        </td>
                        <td className="p-6 border-[#dbdbdb] border-r text-xs text-center whitespace-nowrap">
                          {item?.description}
                        </td>
                        <td className="flex justify-center gap-2 pt-4">
                          <button
                            onClick={() => {
                              onOpenModal()
                              setSelectedItem(item)
                            }}
                          >
                            <BorderColorIcon className="font-light text-[#27004a] text-[10px]] cursor-pointer" />
                          </button>
                          <button onClick={() => setId(item.id)}>
                            <DeleteIcon className="text-red-500 cursor-pointer" />
                          </button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan="17" className="py-4 font-semibold text-gray-600 text-xl text-center">
                      No Lead Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex sm:flex-row flex-col justify-between items-center gap-2 sm:gap-0 p-[16px] pr-0 pb-[0] pl-0 w-[100%]">
            {isLoading ? (
              <PaginationSkeleton />
            ) : (
              <>
                <p className="text-[#a5a1a1] text-[14px]">
                  Showing {(currentleadPage - 1) * 10 + 1} to{" "}
                  {(currentleadPage - 1) * 10 + 10 > totalleadRecords
                    ? totalleadRecords
                    : (currentleadPage - 1) * 10 + 10}{" "}
                  of {totalleadRecords} entries
                </p>
                <div className="flex gap-2 pt-0 pr-4 pb-0 pl-4 border rounded-md pagination">
                  <button
                    onClick={handlePrevious}
                    disabled={currentleadPage === 1}
                    className={`pt-2 pb-[8px] text-[12px] ${currentleadPage === 1 ? "opacity-40" : ""}`}
                  >
                    Previous
                  </button>
                  <span className="flex items-center bg-[#27004a] pr-3 pl-3 text-[12px] text-white">
                    {currentleadPage}
                  </span>
                  <button
                    onClick={handleNext}
                    className={`pt-2 pb-[8px] text-[12px] ${currentleadPage === totalleadPage ? "opacity-40" : ""}`}
                  >
                    Next
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default LeadDetail

